import { Badge } from '@/components/ui/badge'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { RecommendedSetup } from 'app/frontend/base/api/event'
import { CheckCircle2, Info, TriangleAlert } from 'lucide-react'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { Bar, BarChart, Cell, XAxis, YAxis } from 'recharts'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import capitalize from 'lodash/capitalize'
import Card from 'app/frontend/base/components/Card'
import pluralize from 'pluralize'

type Props = {
  activityId: number
  hasLinkedPlanIssues: boolean
  linkedPlanIssueCount: number
}

const roleDistributionChartConfig: ChartConfig = {
  percent: {
    label: 'Share',
    color: 'var(--color-primary)',
  },
}

const roleSetupKeyByRole = {
  tank: 'tanks',
  heal: 'healers',
  melee: 'melee',
  ranged: 'ranged',
} as const
const minimumRecommendedSetupPercent = 10
const baseWowheadUrl = `https://wowhead.com/`

type DistributionDatum = {
  amount: number
  percent: number
}

type DistributionRange = {
  minAmount: number
  maxAmount: number
}

const resolveDistributionRange = (
  recommendedSetup: RecommendedSetup,
): DistributionRange => {
  const allAmounts = [
    ...recommendedSetup.tanks.distribution.map((entry) => entry.amount),
    ...recommendedSetup.healers.distribution.map((entry) => entry.amount),
    ...recommendedSetup.melee.distribution.map((entry) => entry.amount),
    ...recommendedSetup.ranged.distribution.map((entry) => entry.amount),
  ]
  if (allAmounts.length === 0) return { minAmount: 0, maxAmount: 0 }
  return {
    minAmount: Math.min(...allAmounts),
    maxAmount: Math.max(...allAmounts),
  }
}

const resolveDistributionData = (
  recommendedSetup: RecommendedSetup,
  role: typeof characterRoles[number],
  range: DistributionRange,
): DistributionDatum[] => {
  const roleDistribution =
    recommendedSetup[roleSetupKeyByRole[role]].distribution
  const byAmount = new Map(
    roleDistribution.map((entry) => [entry.amount, entry.fraction * 100]),
  )
  return Array.from(
    { length: range.maxAmount - range.minAmount + 1 },
    (_, index) => {
      const amount = range.minAmount + index
      return {
        amount,
        percent: byAmount.get(amount) ?? 0,
      }
    },
  )
}

const resolveSelectedRoleUsagePercent = (
  recommendedSetup: RecommendedSetup,
  role: typeof characterRoles[number],
  selectedCount: number,
) =>
  (recommendedSetup[roleSetupKeyByRole[role]].distribution.find(
    (entry) => entry.amount === selectedCount,
  )?.fraction ?? 0) * 100

const EventActivityIssuesBadge = ({
  activityId,
  hasLinkedPlanIssues,
  linkedPlanIssueCount,
}: Props) => {
  const { activityIssuesById } = useTeamEventParticipantsContext()
  const issues = activityIssuesById.get(activityId)
  if (!issues && linkedPlanIssueCount === 0) return null

  const issueCount = (issues?.issueCount ?? 0) + linkedPlanIssueCount

  if (issueCount === 0 && (!issues || issues?.recommendationCount === 0))
    return (
      <Badge variant='success' className='flex items-center gap-1.5'>
        <CheckCircle2 size={14} />
      </Badge>
    )

  return (
    <HoverCard openDelay={250} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span className='inline-flex cursor-default'>
          {issueCount > 0 ? (
            <Badge variant='warning' className='flex items-center gap-1.5'>
              <TriangleAlert size={14} />
              <span className='leading-none text-xs font-mono'>
                {issueCount}
              </span>
            </Badge>
          ) : issues && issues.recommendationCount > 0 ? (
            <Badge variant='info' className='flex items-center gap-1.5'>
              <Info size={14} />
              <span className='leading-none text-xs font-mono'>
                {issues.recommendationCount}
              </span>
            </Badge>
          ) : null}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side='left'
        align='center'
        className='p-2 text-sm space-y-3 text-left w-fit max-w-72'
      >
        {hasLinkedPlanIssues ? (
          <div>
            <div className='border-b text-left pb-0.5 flex items-center gap-1.5'>
              <TriangleAlert size={12} className='text-warning-foreground' />
              <span>Linked plan</span>
            </div>

            <p className='mt-1 text-xs text-muted-foreground'>
              {linkedPlanIssueCount} issues to resolve
            </p>
          </div>
        ) : null}

        {issues && issues.missingClasses.length > 0 ? (
          <div>
            <div className='border-b text-left pb-0.5 flex items-center gap-1.5'>
              {issues.actionableMissingClasses.length > 0 ? (
                <TriangleAlert size={12} className='text-warning-foreground' />
              ) : (
                <Info size={12} className='text-info-foreground' />
              )}
              <span>Missing classes</span>
            </div>

            <div className='mt-1 flex flex-wrap gap-1.5'>
              {issues.missingClasses.map((characterClass) => (
                <Badge
                  key={characterClass}
                  variant='outline'
                  className='flex items-center gap-1.5'
                >
                  <CharacterClassIcon
                    characterClass={characterClass}
                    size='small'
                    style='round'
                  />
                  <span className={classColours[characterClass].text}>
                    {characterClass}
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        ) : null}

        {issues && issues.tooManyPlayers ? (
          <div>
            <div className='border-b text-left pb-0.5 flex items-center gap-1.5'>
              <TriangleAlert size={12} className='text-warning-foreground' />
              <span>Too many players</span>
            </div>

            <p className='mt-1 text-xs text-muted-foreground'>
              {issues.selectedCount} selected (max {issues.raidSize})
            </p>
          </div>
        ) : null}
        {issues && issues.lootWasteItems.length > 0 ? (
          <div>
            <div className='border-b text-left pb-0.5 flex items-center gap-1.5'>
              <Info size={12} className='text-info-foreground' />
              <span>Potential loot waste</span>
            </div>

            <div className='mt-1 space-y-2'>
              {issues.lootWasteItems.map((item) => (
                <div key={item.id} className='space-y-1'>
                  <a
                    target='_blank'
                    rel='noreferrer'
                    href={`${baseWowheadUrl}item=${item.id}`}
                    className={`inline-flex items-center gap-1.5 text-quality-epic`}
                  >
                    <BlizzardIcon iconName={item.icon} size='small' />
                    <span className='text-xs'>{item.name}</span>
                  </a>

                  <div className='space-y-0.5'>
                    <div className='flex flex-wrap gap-1.5'>
                      <p className='text-xs text-muted-foreground'>
                        Wanted by:
                      </p>
                      {item.wantedBy.map((character) => (
                        <Badge
                          key={`${item.id}:${character.characterId}`}
                          variant='outline'
                          className='flex items-center gap-1.5 pl-0 py-0'
                        >
                          <CharacterClassIcon
                            characterClass={character.characterClass}
                            size='small'
                            style='round'
                          />
                          <span
                            className={
                              classColours[character.characterClass].text
                            }
                          >
                            {character.name}
                          </span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {issues &&
        issues.hasRecommendedSetupMismatch &&
        issues.recommendedSetup ? (
          <div>
            <div className='border-b flex items-center justify-between pb-0.5 gap-8'>
              <div className='flex items-center gap-1.5'>
                <Info size={12} className='text-info-foreground' />
                <span>Recommended setup</span>
              </div>
              <Tooltip>
                <TooltipTrigger>
                  <img
                    src='https://data.wowaudit.com/img/wcl-logo.png'
                    className='w-5 min-w-5'
                  />
                </TooltipTrigger>
                <TooltipContent>
                  Based on the top 50 guild execution kills.
                </TooltipContent>
              </Tooltip>
            </div>

            {(() => {
              const recommendedSetup = issues.recommendedSetup
              if (!recommendedSetup) return null
              return issues.raidSize !== null &&
                issues.selectedCount === issues.raidSize ? (
                <div className='my-1 space-y-0.5'>
                  {characterRoles
                    .map((role) => {
                      const selectedRoleCount = issues.selectedRoleSetup[role]
                      const usagePercent = resolveSelectedRoleUsagePercent(
                        recommendedSetup,
                        role,
                        selectedRoleCount,
                      )
                      return {
                        role,
                        selectedRoleCount,
                        usagePercent,
                      }
                    })
                    .filter(
                      (entry) =>
                        entry.usagePercent < minimumRecommendedSetupPercent,
                    )
                    .map(({ role, selectedRoleCount, usagePercent }) => (
                      <p
                        key={role}
                        className='text-xs text-muted-foreground leading-snug'
                      >
                        You selected {selectedRoleCount}{' '}
                        {pluralize(roleSetupKeyByRole[role], selectedRoleCount)}{' '}
                        ({usagePercent.toFixed(0)}% representation)
                      </p>
                    ))}
                </div>
              ) : null
            })()}

            <div className='mt-1 space-y-1.5'>
              {characterRoles.map((role) => {
                const recommendedSetup = issues.recommendedSetup
                if (!recommendedSetup) return null
                const range = resolveDistributionRange(recommendedSetup)
                const chartData = resolveDistributionData(
                  recommendedSetup,
                  role,
                  range,
                )
                const selectedRoleCount = issues.selectedRoleSetup[role]
                return (
                  <Card
                    key={role}
                    className='flex items-center gap-2 p-0.5 bg-card-nested'
                  >
                    <div className='flex flex-col gap-1.5 w-14 items-end'>
                      <CharacterRoleIcon role={role} size='medium' />
                      <div className='text-xs'>{capitalize(role)}</div>
                    </div>
                    <ChartContainer
                      config={roleDistributionChartConfig}
                      className='h-14 w-60 aspect-auto'
                    >
                      <BarChart
                        data={chartData}
                        margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
                      >
                        <XAxis
                          dataKey='amount'
                          tickLine={false}
                          axisLine={false}
                          tickMargin={1}
                          interval={0}
                          height={18}
                          tick={({ x, y, payload }) => {
                            const amount =
                              typeof payload?.value === 'number'
                                ? payload.value
                                : Number(payload?.value)
                            const isSelectedTick = amount === selectedRoleCount
                            return (
                              <text
                                x={x}
                                y={y}
                                dy={9}
                                textAnchor='middle'
                                className='text-xs'
                                style={{
                                  fill: isSelectedTick
                                    ? 'var(--color-info-foreground)'
                                    : 'var(--color-muted-foreground)',
                                }}
                              >
                                {amount}
                              </text>
                            )
                          }}
                        />
                        <YAxis hide domain={[0, 100]} />
                        <ChartTooltip
                          cursor={false}
                          content={({ active, payload, label }) => {
                            const hoveredPercent =
                              typeof payload?.[0]?.value === 'number'
                                ? payload[0].value
                                : 0
                            if (!active || hoveredPercent === 0) return null
                            return (
                              <ChartTooltipContent
                                active={active}
                                payload={payload}
                                label={label}
                                hideLabel
                                formatter={(value, _name, item) => {
                                  const amount =
                                    typeof item.payload?.amount === 'number'
                                      ? item.payload.amount
                                      : 0
                                  const percent =
                                    typeof value === 'number'
                                      ? `${value.toFixed(1)}%`
                                      : value
                                  return (
                                    <div className='flex w-full items-center justify-between gap-3'>
                                      <span className='text-muted-foreground'>
                                        {amount} players
                                      </span>
                                      <span className='font-mono font-medium text-foreground'>
                                        {percent}
                                      </span>
                                    </div>
                                  )
                                }}
                              />
                            )
                          }}
                        />
                        <Bar
                          dataKey='percent'
                          fill='var(--color-primary)'
                          minPointSize={1}
                        >
                          {chartData.map((entry) => (
                            <Cell
                              key={`${role}-${entry.amount}`}
                              fill={
                                entry.percent === 0
                                  ? 'var(--color-muted-foreground)'
                                  : 'var(--color-primary)'
                              }
                              fillOpacity={entry.percent === 0 ? 0.25 : 1}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  </Card>
                )
              })}
            </div>
          </div>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  )
}

export default EventActivityIssuesBadge
